import { Component, DestroyRef, HostListener, computed, effect, inject } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { CalendarClock, Check, LucideAngularModule, X } from 'lucide-angular';
import { UpgradePromptService } from '../../../core/services/upgrade-prompt.service';
import { CompanyContextService } from '../../../core/services/company-context.service';

/** Başlıkta özellik adının yerini işaretleyen (çeviride görünmeyen) ayraç. */
const FEATURE_SENTINEL = '␟';

/** Paket hiyerarşisi (düşükten yükseğe): lite < basic < pro < premium. */
const PLAN_RANK: Record<string, number> = { lite: 0, basic: 1, pro: 2, premium: 3 };

interface UpgradePlanView {
  key: string;
  popular: boolean;
  nameKey: string;
  priceKey: string;
  descKey: string;
  featureKeys: string[];
}

const PLAN_KEYS = ['basic', 'pro', 'premium'] as const;
const FEATURE_INDEXES = [1, 2, 3, 4, 5];

/**
 * Paket yükseltme (upsell) modalı — uygulama genelinde tek örnek olarak shell'de render edilir.
 * UpgradePromptService.open() sinyaliyle açılır; kilitli bir 'upgrade' özelliği openPrompt()
 * çağırarak bu modalı tetikler. Basic/Pro/Premium fiyat planlarını gösterir; her "Başlayın"
 * müşteri portalına, "Görüşme planlayın" ise Calendly'ye yönlendirir.
 */
@Component({
  selector: 'app-upgrade-modal',
  standalone: true,
  imports: [TranslocoModule, LucideAngularModule],
  templateUrl: './upgrade-modal.component.html',
  styleUrl: './upgrade-modal.component.scss',
})
export class UpgradeModalComponent {
  protected readonly upgradePrompt = inject(UpgradePromptService);
  private readonly transloco = inject(TranslocoService);
  private readonly companyContext = inject(CompanyContextService);

  protected readonly closeIcon = X;
  protected readonly checkIcon = Check;
  protected readonly meetingIcon = CalendarClock;

  private readonly plans: UpgradePlanView[] = PLAN_KEYS.map((key) => ({
    key,
    popular: key === 'pro',
    nameKey: `nav.upgrade.plans.${key}.name`,
    priceKey: `nav.upgrade.plans.${key}.price`,
    descKey: `nav.upgrade.plans.${key}.desc`,
    featureKeys: FEATURE_INDEXES.map((n) => `nav.upgrade.plans.${key}.features.f${n}`),
  }));

  /**
   * Yalnızca mevcut paketten daha üst planları gösterir (upsell). Paket bilinmiyorsa veya
   * daha üst plan yoksa tüm planlar gösterilir (boş dialog olmasın diye).
   */
  protected readonly visiblePlans = computed<UpgradePlanView[]>(() => {
    const current = this.companyContext.companyPackageKey();
    const currentRank = current != null && current in PLAN_RANK ? PLAN_RANK[current] : -1;
    const higher = this.plans.filter((p) => PLAN_RANK[p.key] > currentRank);
    return higher.length ? higher : this.plans;
  });

  constructor() {
    effect(() => this.setScrollLock(this.upgradePrompt.open()));
    inject(DestroyRef).onDestroy(() => this.setScrollLock(false));
  }

  /**
   * Yerelleştirilmiş başlığı özellik adının etrafından ikiye böler; böylece özellik adı
   * şablonda animasyonlu bir etiket (chip) olarak ayrı render edilebilir. Diller arasında
   * kelime sırası farklı olduğundan (ör. TR'de özellik başta) ayraçla bölme kullanılır.
   */
  protected headlineParts(feature: string): { before: string; after: string } {
    const full = this.transloco.translate('nav.upgrade.headlineContext', { feature: FEATURE_SENTINEL });
    const [before, after = ''] = full.split(FEATURE_SENTINEL);
    return { before, after };
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.upgradePrompt.open()) this.upgradePrompt.close();
  }

  private setScrollLock(lock: boolean): void {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('jl-modal-scroll-lock', lock);
  }
}
